import { createSearchParams } from "react-router-dom";
import { wizardStepPaths } from "./common/wizard/wizardStepPaths";
import type { TemplateSection } from "./common/library/templateFromEvent";
import { EditContractStep } from "./common/useQueryParams";
import { WIZARD_STEP_KEYS } from "./common/sourcing-events/buyer/wizardSteps";
import { bidWizardModel } from "./common/sourcing-events/portal/bidWizardSteps";
import { requestWizardModel } from "./common/sourcing-events/portal/requestWizardSteps";
import { contributionWizardModel } from "./common/sourcing-events/portal/contributionWizardSteps";
import { CREATES_PROCUREMENT_PARAM, CREATES_PROCUREMENT_VALUE, PROJECT_PARAM } from "./common/agent/createProcurementParams";

const editPublicBidPaths = (eventId: string, bidId: string) =>
  wizardStepPaths(`/portal/events/${eventId}/tilbud/${bidId}`, bidWizardModel.keys);

const contributionPaths = (pathName: string) => wizardStepPaths(pathName, contributionWizardModel.keys);

const editParticipationRequestPaths = (eventId: string, requestId: string) =>
  wizardStepPaths(`/portal/events/${eventId}/foresporsel/${requestId}`, requestWizardModel.keys);

const editSupplierBidPaths = (eventId: string, bidId: string) =>
  wizardStepPaths(`/konkurranser/${eventId}/tilbud/${bidId}`, bidWizardModel.keys);

const editSupplierRequestPaths = (eventId: string, requestId: string) =>
  wizardStepPaths(`/konkurranser/${eventId}/foresporsel/${requestId}`, requestWizardModel.keys);

const editEventPaths = (id: string) => wizardStepPaths(`/anskaffelser/${id}/rediger`, WIZARD_STEP_KEYS);

const editContractPaths = (id: string, originEvent?: string) => {
  const pathName = `/contracts/${id}/edit`;
  return {
    details: {
      pathname: pathName,
      fullPathName: (step: EditContractStep): string => {
        return `${pathName}?step=${step}`;
      },
      search: createSearchParams({
        step: "details",
        ...(originEvent ? { originEvent } : {}),
      }).toString(),
    },
    documents: {
      pathname: pathName,
      search: createSearchParams({
        step: "docs",
        ...(originEvent ? { originEvent } : {}),
      }).toString(),
    },
    review: {
      pathname: pathName,
      search: createSearchParams({
        step: "review",
        ...(originEvent ? { originEvent } : {}),
      }).toString(),
    },
  };
};

const organizationProfilePaths = (basePath: string) => ({
  pathname: basePath,
  sections: {
    information: "information",
    contracts: "contracts",
    sourcingEvents: "sourcing-events",
    reviewsRisk: "reviews-risk",
  },
  go: (id: string, section = "information") => {
    return `${basePath}/${section}?orgId=${encodeURIComponent(id)}`;
  },
  goToEntry: (id: string, section = "information") => {
    return `${basePath}/${section}?entryId=${encodeURIComponent(id)}`;
  },
  section: (section: string, params: URLSearchParams) => {
    const queryString = params.toString();
    return `${basePath}/${section}${queryString ? "?" + queryString : ""}`;
  },
});

export type OrganizationProfileUrls = ReturnType<typeof organizationProfilePaths>;

export const urls = {
  home: "/",
  emailInvite: "/invite/:emailInviteId",
  projects: {
    viewAll: "/projects",
    view: {
      pathname: "/projects/:projectId",
      go: (projectId: string) => `/projects/${projectId}`,
    },
  },
  legacyEventLinks: {
    view: {
      pathname: "/sourcing/event/:eventId",
    },
    supplierView: {
      pathname: "/sourcing/event/:eventId/supplier-view",
    },
    edit: {
      pathname: "/sourcing/event/:eventId/edit",
    },
    buyerLists: ["/sourcing/events", "/sourcing/events/invitations", "/sourcing/bids/received"],
    supplierLists: ["/sourcing/my-bids"],
    bids: ["/sourcing/bids/:bidId", "/sourcing/bid/:bidId/edit"],
  },
  contracts: {
    edit: {
      paths: editContractPaths(":contractId"),
      go: (id: string, originEvent?: string) => {
        return editContractPaths(id, originEvent);
      },
      isCurrentPage: (): boolean => {
        const regex = /\/contracts\/.*\/edit/;

        return regex.test(window.location.href);
      },
    },
    viewSingle: {
      pathname: "/contracts/:contractId",
      go: (id: string) => {
        return `/contracts/${id}`;
      },
      task: (contractId: string, taskId: string) => {
        return `/contracts/${contractId}?tab=tasks&task=${taskId}`;
      },
      documents: (contractId: string) => {
        return `/contracts/${contractId}?tab=documents`;
      },
      reminders: (contractId: string) => {
        return `/contracts/${contractId}?tab=reminders`;
      },
      dialog: (contractId: string) => {
        return `/contracts/${contractId}?tab=dialog`;
      },
    },
    viewAll: "/contracts",
    viewDrafts: "/contracts/drafts",
    viewPublished: "/contracts/published",
    search: "/contracts/search",
    searchV2: "/contracts/search-v2",
    viewTasks: "/contracts/tasks",
    settings: "/contracts/settings",
  },
  portal: {
    root: "/portal",
    list: "/portal/events",
    view: {
      pathname: "/portal/events/:eventId",
      go: (id: string) => `/portal/events/${id}`,
      question: (eventId: string, questionId: string) => `/portal/events/${eventId}?question=${questionId}`,
      announcement: (eventId: string, announcementId: string) =>
        `/portal/events/${eventId}?announcement=${announcementId}`,
    },
    bid: {
      edit: {
        paths: editPublicBidPaths(":eventId", ":bidId"),
        go: (eventId: string, bidId: string) => editPublicBidPaths(eventId, bidId),
      },
    },
    privateBid: {
      edit: {
        pathname: "/portal/events/:eventId/privat-tilbud/:bidId",
        go: (eventId: string, bidId: string) => `/portal/events/${eventId}/privat-tilbud/${bidId}`,
      },
    },
    marketDialogue: {
      pathname: "/portal/markedsdialog/:marketDialogueId",
      go: (marketDialogueId: string) => `/portal/markedsdialog/${marketDialogueId}`,
    },
    contributions: "/portal/mine-bidrag",
    contribution: {
      pathname: "/portal/bidrag/:contributingEntityId",
      go: (contributingEntityId: string) => `/portal/bidrag/${contributingEntityId}`,
      steps: (contributingEntityId: string) => contributionPaths(`/portal/bidrag/${contributingEntityId}`),
    },
    contributionReview: {
      pathname: "/portal/tilbud/:bidId/bidrag/:contributingEntityId",
      go: (bidId: string, contributingEntityId: string) =>
        `/portal/tilbud/${bidId}/bidrag/${contributingEntityId}`,
      steps: (bidId: string, contributingEntityId: string) =>
        contributionPaths(`/portal/tilbud/${bidId}/bidrag/${contributingEntityId}`),
    },
    request: {
      edit: {
        paths: editParticipationRequestPaths(":eventId", ":requestId"),
        go: (eventId: string, requestId: string) => editParticipationRequestPaths(eventId, requestId),
      },
    },
  },
  admin: "/admin",
  notifications: "/notifications",
  organizations: {
    init: "/organization/init",
    search: "/organizations",
    profile: organizationProfilePaths("/organizations/profile"),
  },
  myProfile: {
    organization: {
      edit: "/organization/edit",
      manageUsers: "/organization/manage-users",
      settings: "/organization/settings",
    },
    personal: {
      view: "/personal-profile",
      edit: "/personal-profile/edit",
    },
  },
  app: {
    home: "/",
    overview: "/oversikt",
    notifications: "/varslinger",
    assistant: "/assistent",
    createProcurementWithAi: (projectId?: string) =>
      `/assistent?${CREATES_PROCUREMENT_PARAM}=${CREATES_PROCUREMENT_VALUE}${
        projectId ? `&${PROJECT_PARAM}=${encodeURIComponent(projectId)}` : ""
      }`,
    needs: {
      new: "/meld-inn-behov",
      edit: {
        pathname: "/meld-inn-behov/:needId",
        go: (needId: string) => `/meld-inn-behov/${needId}`,
      },
      view: {
        pathname: "/behov/:needId",
        go: (needId: string) => `/behov/${needId}`,
      },
      mine: "/mine-behov",
      list: "/behovsliste",
    },
    marketDialogues: {
      viewAll: "/markedsdialoger",
      view: {
        pathname: "/markedsdialoger/:marketDialogueId",
        go: (marketDialogueId: string) => `/markedsdialoger/${marketDialogueId}`,
      },
      formerViewAll: "/anskaffelser/markedsdialoger",
      formerView: "/anskaffelser/markedsdialoger/:marketDialogueId",
    },
    sourcingEvents: {
      viewAll: "/anskaffelser",
      view: {
        pathname: "/anskaffelser/:eventId",
        go: (eventId: string) => `/anskaffelser/${eventId}`,
        question: (eventId: string, questionId: string) =>
          `/anskaffelser/${eventId}?tab=communication&sub=questions&question=${questionId}`,
        announcement: (eventId: string, announcementId: string) =>
          `/anskaffelser/${eventId}?tab=communication&announcement=${announcementId}`,
        evaluation: (eventId: string) => `/anskaffelser/${eventId}?tab=evaluation`,
      },
      protocol: {
        pathname: "/anskaffelser/:eventId/protokoll",
        url: (eventId: string) => `/anskaffelser/${eventId}/protokoll`,
      },
      bidDetail: {
        pathname: "/anskaffelser/:eventId/tilbud/:bidId",
        go: (eventId: string, bidId: string) => `/anskaffelser/${eventId}/tilbud/${bidId}`,
      },
      participationRequestDetail: {
        pathname: "/anskaffelser/:eventId/foresporsler/:requestId",
        go: (eventId: string, requestId: string) => `/anskaffelser/${eventId}/foresporsler/${requestId}`,
      },
      edit: {
        paths: editEventPaths(":eventId"),
        go: (id: string) => editEventPaths(id),
        legacyPathname: "/sourcing-event/public/:eventId/edit",
      },
      editNegotiationRound: {
        pathname: "/anskaffelser/:eventId/forhandlinger/:roundId/rediger",
        go: (eventId: string, roundId: string) => `/anskaffelser/${eventId}/forhandlinger/${roundId}/rediger`,
      },
    },
    contracts: {
      viewAll: "/kontrakter",
      active: "/aktive-kontrakter",
      search: "/kontrakter-sok",
      tasks: "/kontraktsoppgaver",
      reports: "/mine-rapporter",
      view: {
        pathname: "/kontrakter/:contractId",
        go: (contractId: string) => `/kontrakter/${contractId}`,
        task: (contractId: string, taskId: string) => `/kontrakter/${contractId}?tab=tasks&task=${taskId}`,
        documents: (contractId: string) => `/kontrakter/${contractId}?tab=documents`,
        dialog: (contractId: string) => `/kontrakter/${contractId}?tab=dialog`,
      },
      edit: {
        pathname: "/kontrakter/:contractId/rediger",
        go: (contractId: string) => `/kontrakter/${contractId}/rediger`,
      },
    },
    frameworkAgreements: {
      viewAll: "/rammeavtaler",
      view: {
        pathname: "/rammeavtaler/:frameworkAgreementId",
        go: (frameworkAgreementId: string) => `/rammeavtaler/${frameworkAgreementId}`,
      },
    },
    projects: {
      viewAll: "/prosjekter",
      view: {
        pathname: "/prosjekter/:projectId",
        go: (projectId: string) => `/prosjekter/${projectId}`,
      },
    },
    profile: "/profil",
    profileEdit: "/profil/rediger",
    settings: "/innstillinger",
    maler: {
      list: "/maler",
      new: {
        pathname: "/maler/ny",
        // Without arguments this opens a blank template. `fromEvent` pre-fills the editor from a live
        // event's requirements — optionally narrowed to a single section — as an unsaved draft.
        go: (from?: { fromEvent: string; sections?: TemplateSection[] }) => {
          if (!from) return "/maler/ny";
          const search = createSearchParams({
            fromEvent: from.fromEvent,
            ...(from.sections ? { sections: from.sections.join(",") } : {}),
          }).toString();
          return `/maler/ny?${search}`;
        },
      },
      edit: {
        pathname: "/maler/:id",
        go: (id: string) => `/maler/${id}`,
      },
    },
    kravbibliotek: "/kravbibliotek",
    users: "/brukere",
    organizations: "/bedrifter",
    organizationProfile: organizationProfilePaths("/bedrifter/profil"),
    organizationProfileEdit: "/bedrifter/profil/rediger",
    supplier: {
      procurements: "/konkurranser",
      procurement: {
        pathname: "/konkurranser/:eventId",
        go: (eventId: string) => `/konkurranser/${eventId}`,
        question: (eventId: string, questionId: string) => `/konkurranser/${eventId}?question=${questionId}`,
        announcement: (eventId: string, announcementId: string) =>
          `/konkurranser/${eventId}?announcement=${announcementId}`,
      },
      marketDialogue: {
        pathname: "/markedsdialog/:marketDialogueId",
        go: (marketDialogueId: string) => `/markedsdialog/${marketDialogueId}`,
      },
      bids: "/mine-tilbud",
      frameworkAgreements: "/mine-rammeavtaler",
      bid: {
        edit: {
          paths: editSupplierBidPaths(":eventId", ":bidId"),
          go: (eventId: string, bidId: string) => editSupplierBidPaths(eventId, bidId),
        },
      },
      privateBid: {
        edit: {
          pathname: "/konkurranser/:eventId/privat-tilbud/:bidId",
          go: (eventId: string, bidId: string) => `/konkurranser/${eventId}/privat-tilbud/${bidId}`,
        },
      },
      contributions: "/mine-bidrag",
      contribution: {
        pathname: "/bidrag/:contributingEntityId",
        go: (contributingEntityId: string) => `/bidrag/${contributingEntityId}`,
        steps: (contributingEntityId: string) => contributionPaths(`/bidrag/${contributingEntityId}`),
      },
      contributionReview: {
        pathname: "/tilbud/:bidId/bidrag/:contributingEntityId",
        go: (bidId: string, contributingEntityId: string) =>
          `/tilbud/${bidId}/bidrag/${contributingEntityId}`,
        steps: (bidId: string, contributingEntityId: string) =>
          contributionPaths(`/tilbud/${bidId}/bidrag/${contributingEntityId}`),
      },
      invitations: "/mine-invitasjoner",
      request: {
        edit: {
          paths: editSupplierRequestPaths(":eventId", ":requestId"),
          go: (eventId: string, requestId: string) => editSupplierRequestPaths(eventId, requestId),
        },
      },
    },
  },
  login: "/login",
  register: "/register",
  emailVerification: "/confirmation",
  completeRecovery: (flowId: string): string => `/login?flow=${flowId}`,
  loginWithReturnUrl: (returnTo: string): string => `/login?return_to=${encodeURIComponent(returnTo)}`,
  registerWithReturnUrl: (returnTo: string): string => `/register?return_to=${encodeURIComponent(returnTo)}`,
  init: "/init",
};
